// Glossário (docs/17): a instituição escolhe como a plataforma chama oito termos, sempre
// numa lista fechada. Plural e gênero vêm prontos com cada opção e não se configuram.
// O padrão de cada termo é o primeiro da lista: quem não mexe vê o vocabulário de sempre.
import * as z from 'zod/mini'

export type Genero = 'm' | 'f'
/** Uma opção do glossário: singular, plural e gênero do termo (não da pessoa). */
export type Forma = {
  s: string
  p: string
  g: Genero
  /** coletivo: o plural é a própria palavra no singular ("o suporte", nunca "os suporte") */
  coletivo?: boolean
}
/**
 * "Entrada no curso" traz também o estado de quem entrou, o verbo da equipe (pôr alguém para dentro) e o
 * verbo de quem entra, já com o pronome quando é pronominal ("me matricular", mas "assinar").
 */
export type FormaEntrada = Forma & {
  estado: { s: string; p: string }
  verbo: string
  entrar: { me: string; se: string }
}

const f = (s: string, p: string, g: Genero, extra: Partial<Forma> = {}): Forma => ({ s, p, g, ...extra })

export const GLOSSARIO = {
  aluno: {
    rotulo: 'Quem estuda',
    opcoes: {
      aluno: f('aluno', 'alunos', 'm'),
      membro: f('membro', 'membros', 'm'),
      mentorado: f('mentorado', 'mentorados', 'm'),
      participante: f('participante', 'participantes', 'm'),
      cliente: f('cliente', 'clientes', 'm'),
      colaborador: f('colaborador', 'colaboradores', 'm'),
    },
  },
  matricula: {
    rotulo: 'Entrada no curso',
    opcoes: {
      matricula: {
        ...f('matrícula', 'matrículas', 'f'),
        estado: { s: 'matriculado', p: 'matriculados' },
        verbo: 'matricular',
        entrar: { me: 'me matricular', se: 'se matricular' },
      },
      inscricao: {
        ...f('inscrição', 'inscrições', 'f'),
        estado: { s: 'inscrito', p: 'inscritos' },
        verbo: 'inscrever',
        entrar: { me: 'me inscrever', se: 'se inscrever' },
      },
      assinatura: {
        ...f('assinatura', 'assinaturas', 'f'),
        estado: { s: 'assinante', p: 'assinantes' },
        verbo: 'dar a assinatura',
        entrar: { me: 'assinar', se: 'assinar' },
      },
      acesso: {
        ...f('acesso', 'acessos', 'm'),
        estado: { s: 'com acesso', p: 'com acesso' },
        verbo: 'dar acesso',
        entrar: { me: 'ter acesso', se: 'ter acesso' },
      },
    } satisfies Record<string, FormaEntrada>,
  },
  docente: {
    rotulo: 'Quem ensina',
    opcoes: {
      docente: f('docente', 'docentes', 'm'),
      professor: f('professor', 'professores', 'm'),
      mentor: f('mentor', 'mentores', 'm'),
      instrutor: f('instrutor', 'instrutores', 'm'),
      facilitador: f('facilitador', 'facilitadores', 'm'),
      especialista: f('especialista', 'especialistas', 'm'),
    },
  },
  tutor: {
    rotulo: 'Quem acompanha',
    opcoes: {
      tutor: f('tutor', 'tutores', 'm'),
      monitor: f('monitor', 'monitores', 'm'),
      mentor: f('mentor', 'mentores', 'm'),
      orientador: f('orientador', 'orientadores', 'm'),
      suporte: f('suporte', 'suporte', 'm', { coletivo: true }),
    },
  },
  instituicao: {
    rotulo: 'Quem oferece',
    opcoes: {
      instituicao: f('instituição', 'instituições', 'f'),
      escola: f('escola', 'escolas', 'f'),
      academia: f('academia', 'academias', 'f'),
      empresa: f('empresa', 'empresas', 'f'),
      plataforma: f('plataforma', 'plataformas', 'f'),
      comunidade: f('comunidade', 'comunidades', 'f'),
    },
  },
  curso: {
    rotulo: 'O que se estuda',
    opcoes: {
      curso: f('curso', 'cursos', 'm'),
      treinamento: f('treinamento', 'treinamentos', 'm'),
      programa: f('programa', 'programas', 'm'),
      formacao: f('formação', 'formações', 'f'),
      jornada: f('jornada', 'jornadas', 'f'),
    },
  },
  aula: {
    rotulo: 'A unidade de estudo',
    opcoes: {
      aula: f('aula', 'aulas', 'f'),
      licao: f('lição', 'lições', 'f'),
      episodio: f('episódio', 'episódios', 'm'),
      encontro: f('encontro', 'encontros', 'm'),
      conteudo: f('conteúdo', 'conteúdos', 'm'),
    },
  },
  prova: {
    rotulo: 'A prova',
    opcoes: {
      prova: f('prova', 'provas', 'f'),
      avaliacao: f('avaliação', 'avaliações', 'f'),
      teste: f('teste', 'testes', 'm'),
      exame: f('exame', 'exames', 'm'),
    },
  },
} as const

export type Termo = keyof typeof GLOSSARIO
export const TERMOS = Object.keys(GLOSSARIO) as Termo[]
type Opcao<T extends Termo> = keyof (typeof GLOSSARIO)[T]['opcoes'] & string
const chaves = <T extends Termo>(t: T) => Object.keys(GLOSSARIO[t].opcoes) as [Opcao<T>, ...Opcao<T>[]]

/** A escolha da instituição: uma opção da lista por termo. */
export const Glossario = z.object({
  aluno: z.enum(chaves('aluno')),
  matricula: z.enum(chaves('matricula')),
  docente: z.enum(chaves('docente')),
  tutor: z.enum(chaves('tutor')),
  instituicao: z.enum(chaves('instituicao')),
  curso: z.enum(chaves('curso')),
  aula: z.enum(chaves('aula')),
  prova: z.enum(chaves('prova')),
})
export type Glossario = z.infer<typeof Glossario>

/** O padrão: o primeiro de cada linha. */
export const GLOSSARIO_PADRAO: Glossario = {
  aluno: 'aluno',
  matricula: 'matricula',
  docente: 'docente',
  tutor: 'tutor',
  instituicao: 'instituicao',
  curso: 'curso',
  aula: 'aula',
  prova: 'prova',
}

/** Completa o que veio guardado com o padrão, descartando opção que não existe mais. */
export function glossarioDe(g: Partial<Record<Termo, string>> | undefined): Glossario {
  const r = { ...GLOSSARIO_PADRAO } as Record<Termo, string>
  for (const t of TERMOS) {
    const v = g?.[t]
    if (v && v in GLOSSARIO[t].opcoes) r[t] = v
  }
  return r as Glossario
}

export const formaDe = <T extends Termo>(t: T, g: Glossario): Forma =>
  (GLOSSARIO[t].opcoes as Record<string, Forma>)[g[t]] as Forma

// ---------------------------------------------------------------- flexão
// Cada termo vira um conjunto de chaves prontas, no padrão de quem lê o código:
//   t.aluno / t.alunos            → "aluno" / "alunos"
//   t.oAluno / t.osAlunos         → "o aluno" / "os alunos" ("a mentorada" não existe: o gênero é do termo)
//   t.umAluno                     → "um aluno" / "uma escola"
//   t.doAluno, t.noAluno, t.aoAluno, t.peloAluno  (e os plurais dos/nos/aos/pelos)
//   t.esteCurso, t.desteCurso, t.nesteCurso (e esta/desta/nesta): "esta formação", "neste episódio"
//   Os prefixos femininos são sinônimos: t.daAula = t.doAula ("da aula" ou "do episódio").
//   Para "a"/"à" use t.aoAula / t.aosAulas ("à aula", "às aulas", "ao encontro").
//   Inicial maiúscula na chave → inicial maiúscula no texto: t.Aluno, t.OAluno, t.DoCurso.
// Concordância fora do termo: t.g('curso', 'novo', 'nova').

/** O nome de cada termo no código, no singular e no plural (sem acento). */
const NOMES = {
  aluno: ['aluno', 'alunos'],
  matricula: ['matricula', 'matriculas'],
  docente: ['docente', 'docentes'],
  tutor: ['tutor', 'tutores'],
  instituicao: ['instituicao', 'instituicoes'],
  curso: ['curso', 'cursos'],
  aula: ['aula', 'aulas'],
  prova: ['prova', 'provas'],
} as const satisfies Record<Termo, readonly [string, string]>

/** Prefixo da chave → grupo de artigo. As duas grafias valem ("doCurso" e "daAula"): escreva a que lê melhor. */
const PREF_S = {
  o: 'o',
  a: 'o',
  um: 'um',
  uma: 'um',
  do: 'do',
  da: 'do',
  no: 'no',
  na: 'no',
  ao: 'ao',
  pelo: 'pelo',
  pela: 'pelo',
  deUm: 'deUm',
  deUma: 'deUm',
  este: 'este',
  esta: 'este',
  deste: 'deste',
  desta: 'deste',
  neste: 'neste',
  nesta: 'neste',
} as const
const PREF_P = {
  os: 'os',
  as: 'os',
  dos: 'dos',
  das: 'dos',
  nos: 'nos',
  nas: 'nos',
  aos: 'aos',
  pelos: 'pelos',
  pelas: 'pelos',
} as const
type PrefS = keyof typeof PREF_S
type PrefP = keyof typeof PREF_P
type Cap<S extends string> = Capitalize<S>
type ChavesDe<B extends string, Pf extends string> = B | Cap<B> | `${Pf}${Cap<B>}` | `${Cap<Pf>}${Cap<B>}`
type Sing = (typeof NOMES)[Termo][0]
type Plur = (typeof NOMES)[Termo][1]
type ChaveTermo = ChavesDe<Sing, PrefS> | ChavesDe<Plur, PrefP>
type ChaveEntrada =
  | 'matriculado'
  | 'matriculados'
  | 'matricular'
  | 'meMatricular'
  | 'seMatricular'
  | 'Matriculado'
  | 'Matriculados'
  | 'Matricular'
  | 'MeMatricular'
  | 'SeMatricular'

export type Termos = Record<ChaveTermo | ChaveEntrada, string> & {
  /** a escolha da instituição, para comparar ou mostrar */
  glossario: Glossario
  genero: Record<Termo, Genero>
  /** concordância: a palavra masculina ou feminina conforme o gênero do termo */
  g: (t: Termo, masculino: string, feminino: string) => string
}

export const maiuscula = (s: string) => (s ? s.charAt(0).toLocaleUpperCase('pt-BR') + s.slice(1) : s)

/** Artigo e contrações por gênero, no singular e no plural. */
const ARTIGOS: Record<string, Record<Genero, string>> = {
  o: { m: 'o', f: 'a' },
  um: { m: 'um', f: 'uma' },
  do: { m: 'do', f: 'da' },
  no: { m: 'no', f: 'na' },
  ao: { m: 'ao', f: 'à' },
  pelo: { m: 'pelo', f: 'pela' },
  deUm: { m: 'de um', f: 'de uma' },
  este: { m: 'este', f: 'esta' },
  deste: { m: 'deste', f: 'desta' },
  neste: { m: 'neste', f: 'nesta' },
  os: { m: 'os', f: 'as' },
  dos: { m: 'dos', f: 'das' },
  nos: { m: 'nos', f: 'nas' },
  aos: { m: 'aos', f: 'às' },
  pelos: { m: 'pelos', f: 'pelas' },
}
/** No coletivo, o plural usa o artigo do singular. */
const SINGULAR_DE: Record<string, string> = { os: 'o', dos: 'do', nos: 'no', aos: 'ao', pelos: 'pelo' }

const memo = new Map<string, Termos>()

/** As formas prontas para a escolha da instituição. Barato e memorizado: chame à vontade. */
export function termos(entrada?: Partial<Glossario> | null): Termos {
  const glossario = glossarioDe(entrada ?? undefined)
  const chave = JSON.stringify(glossario)
  const pronto = memo.get(chave)
  if (pronto) return pronto
  const r: Record<string, string> = {}
  const genero = {} as Record<Termo, Genero>
  const por = (k: string, v: string) => {
    r[k] = v
    r[maiuscula(k)] = maiuscula(v)
  }
  for (const t of TERMOS) {
    const fm = formaDe(t, glossario)
    genero[t] = fm.g
    const [ns, np] = NOMES[t]
    por(ns, fm.s)
    por(np, fm.p)
    for (const [p, grupo] of Object.entries(PREF_S)) {
      por(`${p}${maiuscula(ns)}`, `${ARTIGOS[grupo]?.[fm.g]} ${fm.s}`)
    }
    for (const [p, grupo] of Object.entries(PREF_P)) {
      const art = ARTIGOS[fm.coletivo ? (SINGULAR_DE[grupo] ?? grupo) : grupo]?.[fm.g]
      por(`${p}${maiuscula(np)}`, `${art} ${fm.p}`)
    }
  }
  const ent = GLOSSARIO.matricula.opcoes[glossario.matricula] as FormaEntrada
  por('matriculado', ent.estado.s)
  por('matriculados', ent.estado.p)
  por('matricular', ent.verbo)
  por('meMatricular', ent.entrar.me)
  por('seMatricular', ent.entrar.se)
  const feito = {
    ...(r as Record<ChaveTermo | ChaveEntrada, string>),
    glossario,
    genero,
    g: (t: Termo, m: string, fe: string) => (genero[t] === 'f' ? fe : m),
  }
  memo.set(chave, feito)
  return feito
}

/** Vocabulário de sempre, para quem não tem a instância à mão (testes, textos de exemplo). */
export const TERMOS_PADRAO = termos(GLOSSARIO_PADRAO)
